import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card } from '../../components/ui/Card';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { restoreRequest } from '../../api/auth';
import { extractErrorMessage } from '../../api/client';
import { restoreSchema, type RestoreInput } from '../../lib/validators';

export function RestorePage() {
  const navigate = useNavigate();
  const [sent, setSent] = useState<{ email: string; guid?: string } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<RestoreInput>({
    resolver: zodResolver(restoreSchema),
    mode: 'onChange',
  });

  const onSubmit = handleSubmit(async ({ email }) => {
    setFormError(null);
    try {
      const res = await restoreRequest(email);
      setSent({ email, guid: res.guid });
    } catch (error) {
      setFormError(extractErrorMessage(error, 'Не удалось отправить письмо'));
    }
  });

  if (sent) {
    return (
      <Card className="flex flex-col gap-4 p-6">
        <h1 className="text-2xl font-semibold">Письмо отправлено</h1>
        <p className="text-sm text-text-muted">
          Мы отправили код на <span className="font-medium text-text">{sent.email}</span>.
          Найдите письмо, откройте ссылку из него или введите код вручную.
        </p>
        <Button
          onClick={() =>
            navigate(`/restore/confirm${sent.guid ? `?guid=${sent.guid}` : ''}`)
          }
          fullWidth
          size="lg"
        >
          Ввести код
        </Button>
        <Link
          to="/login"
          className="text-center text-sm font-medium text-brand-primary hover:underline"
        >
          Вернуться ко входу
        </Link>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col gap-4 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Восстановление пароля</h1>
        <p className="mt-1 text-sm text-text-muted">
          Укажите email — отправим код для восстановления.
        </p>
      </div>

      {formError && <ErrorBanner message={formError} />}

      <form className="flex flex-col gap-3" onSubmit={onSubmit} noValidate>
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          autoFocus
          placeholder="you@example.ru"
          error={errors.email?.message}
          {...register('email')}
        />
        <Button
          type="submit"
          fullWidth
          loading={isSubmitting}
          disabled={!isValid}
          size="lg"
        >
          Отправить письмо
        </Button>
      </form>

      <Link
        to="/login"
        className="text-center text-sm font-medium text-brand-primary hover:underline"
      >
        Вернуться ко входу
      </Link>
    </Card>
  );
}
